import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type {
  ExchangeImpersonationHandoff,
  LoadSession,
  SetSession,
} from '@/modules/auth/use-cases'
import type { LoadMemberContext } from '@/shared/domain'
import { writeImpersonation } from '@/shared/infra/impersonation'

export type UseImpersonationHandoffPageProps = {
  onExchangeImpersonationHandoff: ExchangeImpersonationHandoff
  onSetSession: SetSession
  onLoadSession: LoadSession
  onLoadMemberContext: LoadMemberContext
}

export type ImpersonationHandoffStatus = 'loading' | 'expired' | 'failed'

export type UseImpersonationHandoffPageResult = {
  status: ImpersonationHandoffStatus
  message: string
  onReturnToDashboard: () => void
}

export const LOADING_MESSAGES = [
  'Verifying support session…',
  'Exchanging your code…',
  'Opening the organization…',
  'Redirecting…',
] as const

export const MESSAGE_CYCLE_INTERVAL_MS = 600

const consumedCodes = new Set<string>()

export function useImpersonationHandoffPage({
  onExchangeImpersonationHandoff,
  onSetSession,
  onLoadSession,
  onLoadMemberContext,
}: UseImpersonationHandoffPageProps): UseImpersonationHandoffPageResult {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const hasHandledRef = useRef<boolean>(false)
  const [status, setStatus] = useState<ImpersonationHandoffStatus>('loading')
  const [messageIndex, setMessageIndex] = useState<number>(0)

  useEffect(() => {
    if (hasHandledRef.current) return
    hasHandledRef.current = true

    const code = searchParams.get('code')
    const orgId = searchParams.get('org_id')
    const orgName = searchParams.get('org_name') ?? ''
    const expiresAt = searchParams.get('expires_at') ?? ''

    if (!code || !orgId) {
      setStatus('expired')
      return
    }

    if (expiresAt && new Date(expiresAt).getTime() < Date.now()) {
      setStatus('expired')
      return
    }

    if (consumedCodes.has(code)) {
      return
    }
    consumedCodes.add(code)

    const run = async () => {
      let accessToken: string
      let refreshToken: string
      try {
        const tokens = await onExchangeImpersonationHandoff({ code })
        accessToken = tokens.accessToken
        refreshToken = tokens.refreshToken
      } catch {
        consumedCodes.delete(code)
        setStatus('expired')
        return
      }

      try {
        await onSetSession({ accessToken, refreshToken })
        const user = await onLoadSession()
        if (!user) {
          setStatus('failed')
          return
        }
        const context = await onLoadMemberContext({})
        if (!context?.user?.id) {
          setStatus('failed')
          return
        }
        writeImpersonation({ orgId, orgName, expiresAt })
        queryClient.clear()
        navigate('/dashboard', { replace: true })
      } catch {
        setStatus('failed')
      }
    }

    void run()
  }, [
    navigate,
    onExchangeImpersonationHandoff,
    onLoadMemberContext,
    onLoadSession,
    onSetSession,
    queryClient,
    searchParams,
  ])

  useEffect(() => {
    if (status !== 'loading') return
    const id = window.setInterval(() => {
      setMessageIndex((prev) => Math.min(prev + 1, LOADING_MESSAGES.length - 1))
    }, MESSAGE_CYCLE_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [status])

  const onReturnToDashboard = (): void => {
    navigate('/dashboard', { replace: true })
  }

  return {
    status,
    message: LOADING_MESSAGES[messageIndex] ?? LOADING_MESSAGES[0],
    onReturnToDashboard,
  }
}
